import { ChevronLeft, Trash2 } from "lucide-react";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ProfileMenuOption } from "@/components/profile/ProfileMenuOption";
import { DeleteAccountDialog } from "@/components/profile/DeleteAccountDialog";
import { ExportDataDialog } from "@/components/profile/ExportDataDialog";
import { LANGUAGES } from "@/components/common/language";
import { customerService } from "@/services";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export const SettingPage = memo(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();

  const [currentView, setCurrentView] = useState<"main" | "language">("main");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const currentLang =
    LANGUAGES.find((l) => i18n.language.startsWith(l.value)) || LANGUAGES[0];

  const handleDeleteConfirm = async () => {
    try {
      await customerService.deleteProfile();
      await logout();
      queryClient.removeQueries({ queryKey: ["store", "me"] });
      toast.success(t("profile.delete_account.success"));
      navigate("/login");
    } catch (error) {
      toast.error(t("auth.errors.default"));
    }
  };

  if (currentView === "language") {
    return (
      <div className="flex w-full flex-col h-screen bg-white overflow-hidden max-w-[830px] mx-auto w-full px-4 md:px-10">
        <div className="flex items-center justify-between px-4 py-4 bg-white sticky top-0 z-10">
          <button
            onClick={() => setCurrentView("main")}
            className="flex items-center justify-center p-2 rounded-full hover:bg-gray-100 transition-colors -ml-2"
          >
            <ChevronLeft className="w-6 h-6 text-[#667085]" />
          </button>

          <h1 className="text-[18px] font-semibold text-[#101828] absolute left-1/2 -translate-x-1/2 whitespace-nowrap">
            {t("profile.setting.language", "Language")}
          </h1>

          <div className="w-10" />
        </div>

        <div className="mt-4 flex flex-col gap-3">
          {LANGUAGES.map((lang) => {
            const isSelected = i18n.language?.split("-")[0] === lang.code;
            return (
              <button
                key={lang.code}
                onClick={() => {
                  i18n.changeLanguage(lang.value);
                  setCurrentView("main");
                }}
                className="w-full flex items-center justify-between px-4 py-[14px] bg-[#F9F9FA] rounded-[10px] active:opacity-70 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={lang.flag}
                    className="size-4 rounded-full object-cover"
                    alt={lang.code}
                  />
                  <span className="font-['Be_Vietnam_Pro'] font-normal text-base text-[#101828]">
                    {lang.label}
                  </span>
                </div>
                {isSelected ? (
                  <div className="w-[22px] h-[22px] rounded-full bg-[#6B4EFF] flex items-center justify-center">
                    <div className="w-[8px] h-[8px] rounded-full bg-white" />
                  </div>
                ) : (
                  <div className="w-[22px] h-[22px] rounded-full border-2 border-[#E4E7EC] bg-transparent" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col h-screen bg-white overflow-hidden max-w-[830px] mx-auto w-full px-4 md:px-10">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 bg-white sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center p-2 rounded-full hover:bg-gray-100 transition-colors -ml-2"
        >
          <ChevronLeft className="w-6 h-6 text-[#667085]" />
        </button>

        <h1 className="text-[18px] font-semibold text-[#101828] absolute left-1/2 -translate-x-1/2 whitespace-nowrap">
          {t("profile.menu.settings", "Setting")}
        </h1>

        <div className="w-10" />
      </div>

      {/* Content */}
      <div className="mt-4 flex flex-col gap-3">
        {/* Language Option */}
        <ProfileMenuOption
          key={"language"}
          icon={
            <div className="w-6 h-6 border-2 border-[#667085] rounded-[6px] flex items-center justify-center">
              <span className="text-[10px] font-bold text-[#667085]">文</span>
            </div>
          }
          label={t("profile.setting.language", "Language")}
          rightContent={currentLang.label}
          onClick={() => setCurrentView("language")}
        />

        {/* Data & Privacy Section */}
        <div className="mt-2">
          <h2 className="text-[14px] font-semibold text-[#101828] mb-3 ml-1">
            {t("profile.setting.data_privacy", "Data & Privacy")}
          </h2>

          <ProfileMenuOption
            key={"export-data"}
            icon={
              <div className="w-6 h-6 border-2 border-[#101828] rounded-[6px] flex items-center justify-center">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" x2="12" y1="15" y2="3" />
                </svg>
              </div>
            }
            label={t("profile.setting.export_data", "Export Data")}
            onClick={() => setShowExportDialog(true)}
          />
        </div>
        {/* Danger Actions Section */}
        <div className="mt-2">
          <h2 className="text-[14px] font-semibold text-[#101828] mb-3 ml-1">
            {t("profile.setting.danger_actions", "Danger Actions")}
          </h2>

          <ProfileMenuOption
            key={"delete-account"}
            icon={
              <Trash2 className="w-6 h-6 text-[#F04438]" strokeWidth={1.5} />
            }
            label={t("profile.setting.delete_account", "Delete Account")}
            hideChevron={true}
            onClick={() => setShowDeleteDialog(true)}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteAccountDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />

      {/* Export Data Modal */}
      <ExportDataDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
      />
    </div>
  );
});

SettingPage.displayName = "SettingPage";
